
<template>

    <section class="trial-courses">
        <div class="panel">
            <div class="panel-body" v-if="!selectedCourse">
                <ul class="course-list">
                    <li v-for="course in courses" :key="course.id">
                        <span>{{ course.name }}</span>
                        <div class="buttons">
                            <button class="btn btn-default btn-sm" @click="editCourse(course)">Edit</button>
                            <button class="btn btn-primary btn-sm" @click="openCourse(course)">Open</button>                            
                        </div>
                    </li>
                </ul>
                <button class="btn btn-success btn-sm" @click="showCreateModal = true">Create New Trial Course</button>

            </div>
            <div class="panel-body" v-else-if="!selectedLesson">
                <button class="btn btn-default btn-sm" @click="selectedCourse = null">&larr; Back</button>
                <div class="header">
                    <h4>{{ selectedCourse.name }}</h4>
                    <button class="btn btn-success btn-sm" @click="scheduleCourse()">Schedule New Run</button>
                </div>
                <ul class="lesson-list">
                    <li v-for="lesson in sortedLessons" :key="lesson.id">
                        <span class="lesson-day">{{ lesson.name }}</span>                        
                        <span class="lesson-name">{{ lesson.title }}</span>
                        <button class="btn btn-primary btn-sm" @click="openLesson(lesson)">Open</button>
                    </li>
                </ul>
                <div class="instances" v-if="selectedCourse.instances && selectedCourse.instances.length">
                    <h5 class="bold">Scheduled Runs</h5>
                    <div class="instance well" v-for="instance in selectedCourse.instances" :key="instance.id">
                        <div class="instance-header">
                            <div class="instance-details bold">
                                <span>{{ instance.startDate | formatDate }} &ndash; {{ instance.endDate | formatDate }}</span>
                                <span>{{ instance.slotsFilled }}/{{ instance.slots }} slots filled</span>
                            </div>
                            <div class="instance-actions">
                                <button class="btn btn-success btn-sm" @click="addMeeting(instance)">Add Live Session</button>
                                <button class="btn btn-default btn-sm" @click="openSubmissions(instance)">{{ submissionsInstanceId === instance.id ? 'Hide Submissions' : 'Submissions' }}</button>
                                <button v-if="new Date(instance.startDate) > new Date()" class="btn btn-primary btn-sm" @click="editInstance(instance)">Edit</button>
                                <button v-if="new Date(instance.startDate) > new Date()" class="btn btn-danger btn-sm" @click="deleteInstance(instance)">Delete</button>
                            </div>
                        </div>
                        <ul class="meeting-list" v-if="instance.meetings && instance.meetings.length">
                            <li v-for="meeting in instance.meetings" :key="meeting.id">
                                <div class="meeting-info">
                                    <span class="bold">Day {{meeting.day}}</span>
                                    <span class="meeting-title">{{ meeting.title }}</span>
                                    <span class="meeting-time">{{ formatMeetingRange(meeting) }}</span>
                                </div>
                                <div class="meeting-actions" v-if="new Date(meeting.startTime) > new Date()">
                                    <button class="btn btn-primary btn-sm" @click="editMeeting(meeting, instance)">Edit</button>
                                    <button class="btn btn-danger btn-sm" @click="deleteMeeting(meeting, instance)">Delete</button>
                                </div>
                            </li>
                        </ul>
                        <div v-if="submissionsInstanceId === instance.id" class="submissions">
                            <p v-if="!submissions" class="text-muted">Loading...</p>
                            <p v-else-if="!submissions.length" class="text-muted">No submissions yet</p>
                            <div v-else v-for="group in groupedSubmissions" :key="group.name" class="submission-group">
                                <div class="submission-group-header">
                                    <h6 class="submission-group-name">{{ group.name }}</h6>
                                    <a href="#" @click.prevent="downloadAllSubmissions(group)" class="btn btn-default btn-sm"><i class="fa fa-download"></i> Download all</a>
                                    <a href="#" @click.prevent="downloadSubmissionsSheet(group)" class="btn btn-default btn-sm"><i class="fa fa-file-excel-o"></i> Download sheet</a>
                                </div>
                                <ul class="submission-list">
                                    <li class="submission" v-for="submission in group.items" :key="submission.id"> 
                                        <div class="meta">
                                            <span class="submission-info text-muted">{{submission.user.firstName}} {{submission.user.lastName}}</span>
                                            <span class="submission-info text-muted">{{submission.createdAt | shortDate}}</span>
                                        </div>
                                        <div class="icons">
                                            <a v-if="submission.notes" @click.prevent="openNotes(submission.notes)" href="#" target="_blank" class="btn btn-default btn-sm"><i class="fa fa-sticky-note-o"></i></a>
                                            <a href="#" @click.prevent="downloadSubmission(submission)" class="btn btn-default btn-sm"><i class="fa fa-download"></i></a>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="panel-body" v-else>
                <button class="btn btn-default btn-sm" @click="selectedLesson = null">&larr; Back</button>
                <div class="header">
                    <h4 v-if="selectedLesson.day > 0">Day {{ selectedLesson.day }}</h4>
                    <h4 v-else>Pre-Course</h4>
                    <div class="lesson-actions">
                        <button v-if="!selectedLesson.deliverable" class="btn btn-success btn-sm" @click.prevent="addDeliverable">Add Deliverable</button>
                        <button class="btn btn-success btn-sm" @click.prevent="addDownload">Add Download</button>
                    </div>
                </div>

                <ul class="lesson-tabs">
                    <li :class="{ active: lessonTab === 'information' }" @click="lessonTab = 'information'">Information</li>
                    <li :class="{ active: lessonTab === 'videos' }" @click="lessonTab = 'videos'">Videos</li>
                    <li v-if="selectedLesson.deliverable" :class="{ active: lessonTab === 'deliverable' }" @click="lessonTab = 'deliverable'">Deliverable</li>
                    <li v-if="selectedLesson.downloads && selectedLesson.downloads.length" :class="{ active: lessonTab === 'downloads' }" @click="lessonTab = 'downloads'">Downloads</li>
                    <li :class="{ active: lessonTab === 'notifications' }" @click="lessonTab = 'notifications'">Notifications</li>
                </ul>

                <div v-show="lessonTab === 'information'" class="lesson-information well">
                    <form @submit.prevent="saveLesson">
                        <div class="form-group">
                            <label>Name</label>
                            <input type="text" class="form-control" v-model="lessonForm.name">
                        </div>
                        <div class="form-group">
                            <label>Title</label>
                            <input type="text" class="form-control" v-model="lessonForm.title">
                        </div>
                        <div class="form-group">
                            <label>Description</label>
                            <html-editor
                                :stickyToolbar="true"
                                :tagMap="{ h1: 'h3', h2: 'h4'}"
                                ref="lessonEditor">
                            </html-editor>
                        </div>
                        <button type="submit" class="btn btn-success">Save</button>
                    </form>
                </div>

                <div v-show="lessonTab === 'videos'" class="lesson-videos well">
                    <button class="btn btn-success btn-sm delete" @click.prevent="showAddVideoModal = true">Add Video</button>
                    <draggable v-if="selectedLesson.videos && selectedLesson.videos.length" v-model="selectedLesson.videos" tag="ul" class="videos" handle=".drag-handle" @end="reorderVideos">
                        <li v-for="video in selectedLesson.videos" :key="video.id">
                            <div class="video-title">
                                <i class="fa fa-bars drag-handle"></i>
                                {{ video.title || '(Untitled video)' }}                                
                                <span @click="openVideo(video)" class="cursor-pointer">
                                    <i class="fa fa-edit edit-icon"></i>
                                </span>                                
                                <span @click="deleteVideo(video)" class="cursor-pointer delete-video">
                                    <i class="fa fa-trash"></i>
                                </span>
                                <span v-if="!video.url" v-tooltip="'No video file uploaded'">
                                    <i class="fa fa-exclamation-circle"></i>
                                </span>
                            </div>                            
                        </li>
                    </draggable>
                    <p v-else class="text-muted">No videos yet</p>
                </div>

                <div v-if="selectedLesson.deliverable" v-show="lessonTab === 'deliverable'" class="deliverable well">
                    <button class="btn btn-danger delete btn-sm" @click.prevent="deleteDeliverable">Delete</button>
                    <form @submit.prevent="saveDeliverable">
                        <div class="form-group">
                            <label>Name</label>
                            <input type="text" class="form-control" v-model="deliverableForm.name">
                        </div>
                        <div class="form-group">
                            <label>Description</label>
                            <html-editor
                                :stickyToolbar="true"
                                :tagMap="{ h1: 'h3', h2: 'h4'}"
                                ref="deliverableEditor">
                            </html-editor>
                        </div>
                        <div class="form-group">
                            <label>
                                <input type="checkbox" v-model="deliverableForm.isFinal">
                                Final deliverable
                            </label>
                        </div>
                        <button type="submit" class="btn btn-success">Save Deliverable</button>
                    </form>
                </div>

                <div v-if="selectedLesson.downloads && selectedLesson.downloads.length" v-show="lessonTab === 'downloads'" class="downloads well">
                    <ul class="download-list">
                        <li v-for="download in selectedLesson.downloads" :key="download.id">
                            <input type="text" class="form-control" v-model="download.name">
                            <div class="download-actions">
                                <button class="btn btn-success btn-sm" @click.prevent="saveDownload(download)">Save</button>
                                <a class="btn btn-default btn-sm" href="#" @click.prevent="downloadFile(download.url)">Download</a>
                                <label class="btn btn-default btn-sm">
                                    {{ download.thumbnail ? 'Change Thumbnail' : 'Add Thumbnail' }}
                                    <input type="file" accept="image/*" style="display:none" @change="setDownloadThumbnail(download, $event)">
                                </label>
                                <button class="btn btn-danger btn-sm" @click.prevent="deleteDownload(download)">Delete</button>
                            </div>
                        </li>
                    </ul>
                </div>

                <div v-show="lessonTab === 'notifications'" class="notifications well">
                    <button class="btn btn-success btn-sm" @click.prevent="openAddNotificationModal">Add Notification</button>
                    <ul v-if="selectedLesson.notifications && selectedLesson.notifications.length" class="notification-list">
                        <li v-for="notification in selectedLesson.notifications" :key="notification.id">
                            <div v-if="notification.color" class="notification-color-swatch" :style="{ background: notification.color }"></div>
                            <span class="notification-header">{{ notification.header }}</span>
                            <div class="notification-actions">
                                <button class="btn btn-primary btn-sm" @click.prevent="openEditNotificationModal(notification)">Edit</button>
                                <button class="btn btn-danger btn-sm" @click.prevent="deleteNotification(notification)">Delete</button>
                            </div>
                        </li>
                    </ul>
                    <p v-else class="text-muted" style="margin-top:1rem">No notifications yet</p>
                </div>
            </div>
        </div>

        <div class="overlay" v-if="showCreateModal" @click.prevent="showCreateModal = false">
            <div class="modal-box nice-scrollbar modal-box-lg" @click.stop>
                <h4>Create Trial Course</h4>
                <form @submit.prevent="createCourse">
                    <div class="form-group">
                        <label>Name</label>
                        <input type="text" class="form-control" v-model="form.name" required>
                    </div>
                    <div class="form-group">
                        <label>Duration (days)</label>
                        <input type="number" class="form-control" v-model.number="form.duration" required>
                    </div>
                    <div class="form-group">
                        <label>Description</label>
                        <html-editor
                            :stickyToolbar="true"
                            :tagMap="{ h1: 'h3', h2: 'h4'}"
                            ref="createCourseEditor">
                        </html-editor>
                    </div>
                    <div class="form-group">
                        <label class="video-upload">
                            {{ form.introVideoFileName || 'Upload intro video' }}
                            <input type="file" accept="video/mp4" @change="setCreateCourseIntroVideo">
                        </label>
                        <div class="text-center" v-if="form.introVideoFileName">
                            <span class="filename">{{ form.introVideoFileName }}<i @click.prevent="clearCreateCourseIntroVideo" class="fa fa-remove"></i></span>
                        </div>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-default" @click="showCreateModal = false">Cancel</button>
                        <button type="submit" class="btn btn-success">Create</button>
                    </div>
                </form>
            </div>
        </div>

        <div class="overlay" v-if="showEditCourseModal" @click.prevent="showEditCourseModal = false">
            <div class="modal-box nice-scrollbar modal-box-lg" @click.stop>
                <h4>Edit Course</h4>
                <form @submit.prevent="submitEditCourse">
                    <div class="form-group">
                        <label>Description</label>
                        <html-editor
                            :stickyToolbar="true"
                            :tagMap="{ h1: 'h3', h2: 'h4'}"
                            ref="editCourseEditor">
                        </html-editor>
                    </div>
                    <div v-if="editCourseForm.existingIntroVideoUrl" class="form-group">
                        <label>Current Intro Video</label>
                        <div class="video-wrapper">
                            <div class="video">
                                <poly-video
                                    v-if="editCourseForm.introVideoPreviewUrl"
                                    :sources="[ editCourseForm.introVideoPreviewUrl ]"
                                    :vjsOptions="videoPlayerVjsOptions">
                                </poly-video>
                            </div>
                        </div>
                    </div>
                    <div class="form-group">
                        <label class="video-upload">
                            {{ editCourseForm.introVideoFileName || 'Upload intro video' }}
                            <input type="file" accept="video/mp4" @change="setEditCourseIntroVideo">
                        </label>
                        <div class="text-center" v-if="editCourseForm.introVideoFileName">
                            <span class="filename">{{ editCourseForm.introVideoFileName }}<i @click.prevent="clearEditCourseIntroVideo" class="fa fa-remove"></i></span>
                        </div>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-default" @click="showEditCourseModal = false">Cancel</button>
                        <button type="submit" class="btn btn-success">Save</button>
                    </div>
                </form>
            </div>
        </div>

        <div class="overlay" v-if="showAddVideoModal" @click.prevent="showAddVideoModal = false">
            <div class="modal-box nice-scrollbar" @click.stop>
                <h4>Add Video</h4>
                <form @submit.prevent="submitAddVideo">
                    <div class="form-group">
                        <label>Title</label>
                        <input type="text" class="form-control" v-model="addVideoForm.title" required>
                    </div>
                    <div class="form-group">
                        <label class="video-upload">
                            {{ addVideoForm.fileName || 'Upload video' }}
                            <input type="file" accept="video/mp4" @change="setAddVideoFile">
                        </label>
                        <div class="text-center" v-if="addVideoForm.fileName">
                            <span class="filename">{{ addVideoForm.fileName }}<i @click.prevent="clearAddVideoFile" class="fa fa-remove"></i></span>
                        </div>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-default" @click="showAddVideoModal = false">Cancel</button>
                        <button type="submit" class="btn btn-success">Add</button>
                    </div>
                </form>
            </div>
        </div>

        <div class="overlay" v-if="selectedVideo" @click.prevent="selectedVideo = null">
            <div class="modal-box nice-scrollbar modal-box-lg" @click.stop>
                <div class="video-header">
                    <h4>{{ selectedVideo.title || '(Untitled video)' }}</h4>
                    <i v-if="selectedVideo.transcript" title="Show/hide Transcript" class="fa fa-file-text-o toggle-transcript" @click.prevent="showTranscript = !showTranscript"></i>
                </div>
                <div v-if="selectedVideo.transcript && showTranscript" class="transcript">
                    <span v-for="line in selectedVideo.transcript.text">{{line.text}} </span>
                </div>
                <form @submit.prevent="saveVideo">
                    <div class="form-group">
                        <label>Title</label>
                        <input type="text" class="form-control" v-model="videoForm.title">
                    </div>
                    <div v-if="selectedVideo.url" class="video-wrapper">
                        <div class="video">
                            <poly-video
                                v-if="videoUrl"
                                ref="videoPlayer"
                                :sources="[ videoUrl ]"
                                :vjsOptions="videoPlayerVjsOptions"
                                >
                            </poly-video>
                        </div>
                    </div>
                    <div class="form-group">
                        <label class="video-upload">
                            Upload video
                            <input type="file" accept="video/mp4" @change="setFile">
                        </label>
                        <div class="text-center" v-if="videoForm.fileName">
                            <span class="filename">{{videoForm.fileName}}<i @click.prevent="clearFile()" class="fa fa-remove"></i></span>
                        </div>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-default" @click="selectedVideo = null">Cancel</button>
                        <button type="submit" class="btn btn-success">Save</button>
                    </div>
                </form>
            </div>
        </div>

        <div class="overlay" v-if="showScheduleModal" @click.prevent="showScheduleModal = false">
            <div class="modal-box nice-scrollbar" @click.stop>
                <h4>Schedule Course Run</h4>
                <form @submit.prevent="submitSchedule">
                    <div class="form-group">
                        <label>Start Date</label>
                        <input type="date" class="form-control" v-model="scheduleForm.startDate" required>
                    </div>
                    <div class="form-group">
                        <label>Slots</label>
                        <input type="number" class="form-control" v-model.number="scheduleForm.slots" min="1" required>
                    </div>
                    <div class="form-group">
                        <label>Start Time</label>
                        <input type="time" class="form-control" v-model="scheduleForm.startTime" @change="autoSetEndTime" required>
                    </div>
                    <div class="form-group">
                        <label>Timezone</label>
                        <select class="form-control" v-model="scheduleForm.timezone" required>
                            <option v-for="tz in timeZones" :key="tz.value" :value="tz.value">{{ tz.label }}</option>
                        </select>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-default" @click="showScheduleModal = false">Cancel</button>
                        <button type="submit" class="btn btn-success">Schedule</button>
                    </div>
                </form>
            </div>
        </div>

        <div class="overlay" v-if="showEditInstanceModal" @click.prevent="showEditInstanceModal = false">
            <div class="modal-box nice-scrollbar" @click.stop>
                <h4>Edit Course Run</h4>
                <form @submit.prevent="submitEditInstance">
                    <div class="form-group">
                        <label>Start Date</label>
                        <input type="date" class="form-control" v-model="editInstanceForm.startDate" required>
                    </div>
                    <div class="form-group">
                        <label>Slots</label>
                        <input type="number" class="form-control" v-model.number="editInstanceForm.slots" min="1" required>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-default" @click="showEditInstanceModal = false">Cancel</button>
                        <button type="submit" class="btn btn-success">Save</button>
                    </div>
                </form>
            </div>
        </div>

        <div class="overlay" v-if="showAddDownloadModal" @click.prevent="showAddDownloadModal = false">
            <div class="modal-box nice-scrollbar" @click.stop>
                <h4>Add Download</h4>
                <form @submit.prevent="submitAddDownload">
                    <div class="form-group">
                        <label>Name</label>
                        <input type="text" class="form-control" v-model="downloadForm.name" required>
                    </div>
                    <div class="form-group">
                        <label>Source</label>
                        <div>
                            <label class="radio-inline"><input type="radio" v-model="downloadForm.useUrl" :value="false"> Upload file</label>
                            <label class="radio-inline" style="margin-left:12px"><input type="radio" v-model="downloadForm.useUrl" :value="true"> URL</label>
                        </div>
                    </div>
                    <div class="form-group" v-if="!downloadForm.useUrl">
                        <label class="video-upload">
                            {{ downloadForm.fileName || 'Choose file' }}
                            <input type="file" @change="setDownloadFile">
                        </label>
                        <div class="text-center" v-if="downloadForm.fileName">
                            <span class="filename">{{ downloadForm.fileName }}<i @click.prevent="clearDownloadFile" class="fa fa-remove"></i></span>
                        </div>
                    </div>
                    <div class="form-group" v-if="downloadForm.useUrl">
                        <label>URL</label>
                        <input type="url" class="form-control" v-model="downloadForm.url" placeholder="https://..." required>
                    </div>
                    <div class="form-group">
                        <label>Thumbnail <span class="text-muted">(optional)</span></label>
                        <label class="video-upload">
                            {{ downloadForm.thumbnailName || 'Choose image' }}
                            <input type="file" accept="image/*" @change="setThumbnailFile">
                        </label>
                        <div class="text-center" v-if="downloadForm.thumbnailName">
                            <span class="filename">{{ downloadForm.thumbnailName }}<i @click.prevent="clearThumbnailFile" class="fa fa-remove"></i></span>
                        </div>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-default" @click="showAddDownloadModal = false">Cancel</button>
                        <button type="submit" class="btn btn-primary">Add</button>
                    </div>
                </form>
            </div>
        </div>
        <div class="overlay" v-if="showAddMeetingModal" @click.prevent="showAddMeetingModal = false">
            <div class="modal-box nice-scrollbar" @click.stop>
                <h4>Add Live Session</h4>
                <form @submit.prevent="submitAddMeeting">
                    <div class="form-group">
                        <label>Day</label>
                        <select class="form-control" v-model.number="meetingForm.day" @change="onMeetingDayChange" required>
                            <option value="" disabled>Select a day</option>
                            <option :value="0">Pre-course</option>
                            <option v-for="i in sortedLessons.length" :key="i" :value="i">
                                {{ 'Day ' + i}}
                            </option>
                        </select>
                    </div>
                    <div class="form-group" v-if="meetingForm.day === 0">
                        <label>Date</label>
                        <input type="date" class="form-control" v-model="meetingForm.date" required>
                    </div>
                    <div class="form-group">
                        <label>Title</label>
                        <input type="text" class="form-control" v-model="meetingForm.title" required>
                    </div>                   
                    <div class="form-group">
                        <label>Start Time</label>
                        <input type="time" class="form-control" v-model="meetingForm.startTime" @change="autoSetEndTime" required>
                    </div>
                    <div class="form-group">
                        <label>End Time</label>
                        <input type="time" class="form-control" v-model="meetingForm.endTime" required>
                    </div>
                    <div class="form-group">
                        <label>Host</label>
                        <select v-if="staffMembers" class="form-control" v-model="meetingForm.hostId" required>
                            <option value="" disabled>Select a host</option>
                            <option v-for="member in staffMembers" :value="member.username">
                                {{member.firstName}} {{member.lastName}}
                            </option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label>Timezone</label>
                        <select class="form-control" v-model="meetingForm.timezone" required>
                            <option v-for="tz in timeZones" :key="tz.value" :value="tz.value">{{ tz.label }}</option>
                        </select>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-default" @click="showAddMeetingModal = false">Cancel</button>
                        <button type="submit" class="btn btn-primary">Add</button>
                    </div>
                </form>
            </div>
        </div>

        <div class="overlay" v-if="showAddNotificationModal" @click.prevent="showAddNotificationModal = false">
            <div class="modal-box nice-scrollbar modal-box-lg" @click.stop>
                <h4>Add Notification</h4>
                <form @submit.prevent="submitAddNotification">
                    <div class="form-group">
                        <label>Header</label>
                        <input type="text" class="form-control" v-model="notificationForm.header" required>
                    </div>
                    <div class="form-group">
                        <label>Text</label>
                        <html-editor
                            :stickyToolbar="true"
                            :tagMap="{ h1: 'h3', h2: 'h4'}"
                            ref="notificationEditor">
                        </html-editor>
                    </div>
                    <div class="form-group">
                        <label>Color <span class="text-muted">(optional)</span></label>
                        <div class="color-picker">
                            <div class="color-swatch no-color" :class="{ selected: !notificationForm.color }" @click="notificationForm.color = null">None</div>
                            <div
                                v-for="(hex, name) in colors"
                                :key="name"
                                class="color-swatch"
                                :class="{ selected: notificationForm.color === hex }"
                                :style="{ background: hex }"
                                :title="name"
                                @click="notificationForm.color = hex">
                            </div>
                        </div>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-default" @click="showAddNotificationModal = false">Cancel</button>
                        <button type="submit" class="btn btn-success">Add</button>
                    </div>
                </form>
            </div>
        </div>

        <div class="overlay" v-if="showEditNotificationModal" @click.prevent="showEditNotificationModal = false">
            <div class="modal-box nice-scrollbar modal-box-lg" @click.stop>
                <h4>Edit Notification</h4>
                <form @submit.prevent="submitEditNotification">
                    <div class="form-group">
                        <label>Header</label>
                        <input type="text" class="form-control" v-model="editNotificationForm.header" required>
                    </div>
                    <div class="form-group">
                        <label>Text</label>
                        <html-editor
                            :stickyToolbar="true"
                            :tagMap="{ h1: 'h3', h2: 'h4'}"
                            ref="editNotificationEditor">
                        </html-editor>
                    </div>
                    <div class="form-group">
                        <label>Color <span class="text-muted">(optional)</span></label>
                        <div class="color-picker">
                            <div class="color-swatch no-color" :class="{ selected: !editNotificationForm.color }" @click="editNotificationForm.color = null">None</div>
                            <div
                                v-for="(hex, name) in colors"
                                :key="name"
                                class="color-swatch"
                                :class="{ selected: editNotificationForm.color === hex }"
                                :style="{ background: hex }"
                                :title="name"
                                @click="editNotificationForm.color = hex">
                            </div>
                        </div>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-default" @click="showEditNotificationModal = false">Cancel</button>
                        <button type="submit" class="btn btn-success">Save</button>
                    </div>
                </form>
            </div>
        </div>

        <div class="overlay" v-if="showNotesModal" @click.prevent="showNotesModal = false">
            <div class="modal-box nice-scrollbar modal-box-lg" @click.stop>
                <h4>Submission Notes</h4>
                <p class="notes-text">{{ notesModalText }}</p>
                <div class="modal-actions">
                    <button type="button" class="btn btn-default" @click="showNotesModal = false">Close</button>
                </div>
            </div>
        </div>

        <div class="overlay" v-if="showEditMeetingModal" @click.prevent="showEditMeetingModal = false">
            <div class="modal-box nice-scrollbar" @click.stop>
                <h4>Edit Live Session</h4>
                <form @submit.prevent="submitEditMeeting">
                    <div class="form-group">
                        <label>Title</label>
                        <input type="text" class="form-control" v-model="editMeetingForm.title" required>
                    </div>
                    <div class="form-group" v-if="editMeetingForm.day === 0">
                        <label>Date</label>
                        <input type="date" class="form-control" v-model="editMeetingForm.date" required>
                    </div>
                    <div class="form-group">
                        <label>Start Time</label>
                        <input type="time" class="form-control" v-model="editMeetingForm.startTime" required>
                    </div>
                    <div class="form-group">
                        <label>End Time</label>
                        <input type="time" class="form-control" v-model="editMeetingForm.endTime" required>
                    </div>
                    <div class="form-group">
                        <label>Host</label>
                        <select v-if="staffMembers" class="form-control" v-model="editMeetingForm.hostId">
                            <option value="" disabled>Select a host</option>
                            <option v-for="member in staffMembers" :value="member.username">
                                {{member.firstName}} {{member.lastName}}
                            </option>
                        </select>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-default" @click="showEditMeetingModal = false">Cancel</button>
                        <button type="submit" class="btn btn-success">Save</button>
                    </div>
                </form>
            </div>
        </div>
    </section>

</template>

<script>
import trialCourses from '../services/trial-courses'
import users from '../services/users'
import aws from '../services/aws'
import toastr from 'toastr'
import S3Utils from '../packages/s3-utils'
import PolyVideo from './reusable/poly-video'
import draggable from 'vuedraggable'
import HtmlEditor from './reusable/html-editor';
import * as fflate from 'fflate';
import streamSaver from 'streamsaver';

const uuidV4 = require('uuid/v4');
const colors = {
'grey-00':'#fafafa',
'grey-50':'#f5f5f5',
'grey-100':'#ebebeb',
'grey-150':'#e0e0e0',
'grey-200':'#d6d6d6',
'grey-250':'#c9c9c9',
'grey-300':'#bdbdbd',
'grey-350':'#b0b0b0',
'grey-400':'#a3a3a3',
'grey-450':'#919191',
'grey-500':'#808080',
'grey-550':'#707070',
'grey-600':'#616161',
'grey-650':'#545454',
'grey-700':'#474747',
'grey-750':'#3b3b3b',
'grey-800':'#303030',
'grey-850':'#262626',
'grey-900':'#1e1e1e',
'grey-950':'#121212',
'grey-1000':'#0a0a0a',
'purple-50':'#faf5ff',
'purple-100':'#f1e8fa',
'purple-200':'#d4b6ee',
'purple-300':'#bf93e6',
'purple-400':'#a162db',
'purple-500':'#7314c9',
'purple-600':'#6912b7',
'purple-700':'#520e8f',
'purple-800':'#3f0b6f',
'purple-900':'#300854',
'purple-1000':'#22063c',
'purple-1100':'#160427',
'purple-1200':'#0f031b',
'pink-50':'#fff5f9',
'pink-100':'#fae8f0',
'pink-200':'#f8a5c9',
'pink-300':'#f68fbc',
'pink-400':'#f262a1',
'pink-500':'#ed1f79',
'pink-600':'#be1961',
'pink-700':'#a61655',
'pink-800':'#8e1349',
'pink-900':'#77103d',
'pink-1000':'#5f0c30',
'pink-1100':'#470924',
'pink-1200':'#2f0618',
'blue-50':'#f6fdff',
'blue-100':'#e6f8fe',
'blue-200':'#b0e8fa',
'blue-300':'#8addf8',
'blue-400':'#54cef5',
'blue-500':'#00b6f0',
'blue-600':'#00a6da',
'blue-700':'#0081aa',
'blue-800':'#006484',
'blue-900':'#004c65',
'blue-1000':'#003141',
'blue-1100':'#001d27',
'blue-1200':'#00141a',
'orange-50':'#fff9f4',
'orange-100':'#fff3ea',
'orange-200':'#ffd8bc',
'orange-300':'#ffc69c',
'orange-400':'#ffab6f',
'orange-500':'#ff8228',
'orange-600':'#e87624',
'orange-700':'#b55c1c',
'orange-800':'#8c4816',
'orange-900':'#6b3711',
'orange-1000':'#4f2506',
'orange-1100':'#28200c',
'orange-1200':'#191407',
'lime-50':'#fcfff2',
'lime-100':'#f9ffe6',
'lime-200':'#ebfeb0',
'lime-300':'#e2fe8a',
'lime-400':'#d5fd54',
'lime-500':'#c0fc00',
'lime-600':'#afe500',
'lime-700':'#88b300',
'lime-800':'#6a8b00',
'lime-900':'#516a00',
'lime-1000':'#374800',
'lime-1100':'#1b2400',
'lime-1200':'#131900',
'teal-50':'#f7fffd',
'teal-100':'#e6fdf8',
'teal-200':'#b0f8eb',
'teal-300':'#8af5e1',
'teal-400':'#54f0d3',
'teal-500':'#00e9bd',
'teal-600':'#00d4ac',
'teal-700':'#00a586',
'teal-800':'#008068',
'teal-900':'#00624f',
'teal-1000':'#004235',
'teal-1100':'#002820',
'teal-1200':'#001a15',
'error-50':'#fff9fa',
'error-100':'#feedef',
'error-200':'#fbc8cd',
'error-300':'#f9aeb4',
'error-400':'#f68992',
'error-500':'#f14f5d',
'error-600':'#db4855',
'error-700':'#ab3842',
'error-800':'#852b33',
'error-900':'#652127',
'error-1000':'#44181c',
'success-50':'#f6fefa',
'success-100':'#ecf8f2',
'success-200':'#c3e9d5',
'success-300':'#a5dec1',
'success-400':'#7ccfa5',
'success-500':'#3cb878',
'success-600':'#37a76d',
'success-700':'#2b8355',
'success-800':'#216542',
'success-900':'#194d32',
'success-1000':'#113422',
'warning-50':'#fffdfa',
'warning-100':'#fdf8ed',
'warning-200':'#f9eac6',
'warning-300':'#f6e0aa',
'warning-400':'#efc96b',
'warning-500':'#ebbc46',
'warning-600':'#d6ab40',
'warning-700':'#a78532',
'warning-800':'#816727',
'warning-900':'#634f1d',
'warning-1000':'#3e3213',
}


export default {
    data() {
        return {
            courses: [],
            selectedCourse: null,
            selectedLesson: null,
            selectedVideo: null,
            showTranscript: false,
            lessonTab: 'information',
            videoUrl: null,
            showCreateModal: false,            
            showNotesModal: false,            
            notesModalText: '',
            showScheduleModal: false,
            scheduleForm: {
                startDate: '',
                slots: null,
                startTime: '09:00',
                timezone: 'America/Vancouver'
            },
            scheduleCourseId: null,
            showEditInstanceModal: false,
            editInstanceId: null,
            editInstanceForm: {
                startDate: '',
                slots: null
            },
            showAddNotificationModal: false,
            notificationForm: {
                header: '',
                text: '',
                color: null
            },
            showEditNotificationModal: false,
            editNotificationId: null,
            editNotificationRef: null,
            editNotificationForm: {
                header: '',
                text: '',
                color: null
            },
            showAddVideoModal: false,
            addVideoForm: {
                title: '',
                file: null,
                fileName: null
            },
            showAddDownloadModal: false,
            showAddMeetingModal: false,
            staffMembers: null,
            timeZones: [{"label":"Adelaide","value":"Australia/Adelaide"},{"label":"Aden","value":"Asia/Aden"},{"label":"Algiers","value":"Africa/Algiers"},{"label":"Almaty","value":"Asia/Almaty"},{"label":"Amman","value":"Asia/Amman"},{"label":"Amsterdam","value":"Europe/Amsterdam"},{"label":"Anchorage","value":"America/Anchorage"},{"label":"Apia","value":"Pacific/Apia"},{"label":"Araguaina","value":"America/Araguaina"},{"label":"Athens","value":"Europe/Athens"},{"label":"Atlantic","value":"Canada/Atlantic"},{"label":"Auckland","value":"Pacific/Auckland"},{"label":"Azores","value":"Atlantic/Azores"},{"label":"Baghdad","value":"Asia/Baghdad"},{"label":"Bahrain","value":"Asia/Bahrain"},{"label":"Baku","value":"Asia/Baku"},{"label":"Bangkok","value":"Asia/Bangkok"},{"label":"Bangui","value":"Africa/Bangui"},{"label":"Beirut","value":"Asia/Beirut"},{"label":"Belgrade","value":"Europe/Belgrade"},{"label":"Berlin","value":"Europe/Berlin"},{"label":"Bogota","value":"America/Bogota"},{"label":"Brisbane","value":"Australia/Brisbane"},{"label":"Brussels","value":"Europe/Brussels"},{"label":"Bucharest","value":"Europe/Bucharest"},{"label":"Budapest","value":"Europe/Budapest"},{"label":"Buenos_Aires","value":"America/Argentina/Buenos_Aires"},{"label":"Cairo","value":"Africa/Cairo"},{"label":"Calcutta","value":"Asia/Calcutta"},{"label":"Cape_Verde","value":"Atlantic/Cape_Verde"},{"label":"Caracas","value":"America/Caracas"},{"label":"Casablanca","value":"Africa/Casablanca"},{"label":"CET","value":"CET"},{"label":"Chicago","value":"America/Chicago"},{"label":"Copenhagen","value":"Europe/Copenhagen"},{"label":"Costa_Rica","value":"America/Costa_Rica"},{"label":"Dacca","value":"Asia/Dacca"},{"label":"Damascus","value":"Asia/Damascus"},{"label":"Darwin","value":"Australia/Darwin"},{"label":"Denver","value":"America/Denver"},{"label":"Dhaka","value":"Asia/Dhaka"},{"label":"Djibouti","value":"Africa/Djibouti"},{"label":"Dubai","value":"Asia/Dubai"},{"label":"Dublin","value":"Europe/Dublin"},{"label":"Edmonton","value":"America/Edmonton"},{"label":"El_Salvador","value":"America/El_Salvador"},{"label":"Fiji","value":"Pacific/Fiji"},{"label":"Godthab","value":"America/Godthab"},{"label":"Greenwich","value":"Etc/Greenwich"},{"label":"Guatemala","value":"America/Guatemala"},{"label":"Halifax","value":"America/Halifax"},{"label":"Harare","value":"Africa/Harare"},{"label":"Helsinki","value":"Europe/Helsinki"},{"label":"Hobart","value":"Australia/Hobart"},{"label":"Hong_Kong","value":"Asia/Hong_Kong"},{"label":"Honolulu","value":"Pacific/Honolulu"},{"label":"Indianapolis","value":"America/Indianapolis"},{"label":"Irkutsk","value":"Asia/Irkutsk"},{"label":"Istanbul","value":"Europe/Istanbul"},{"label":"Jakarta","value":"Asia/Jakarta"},{"label":"Jerusalem","value":"Asia/Jerusalem"},{"label":"Johannesburg","value":"Africa/Johannesburg"},{"label":"Kabul","value":"Asia/Kabul"},{"label":"Kamchatka","value":"Asia/Kamchatka"},{"label":"Kathmandu","value":"Asia/Kathmandu"},{"label":"Khartoum","value":"Africa/Khartoum"},{"label":"Kiev","value":"Europe/Kiev"},{"label":"Kolkata","value":"Asia/Kolkata"},{"label":"Krasnoyarsk","value":"Asia/Krasnoyarsk"},{"label":"Kuala_Lumpur","value":"Asia/Kuala_Lumpur"},{"label":"Kuwait","value":"Asia/Kuwait"},{"label":"Lima","value":"America/Lima"},{"label":"Lisbon","value":"Europe/Lisbon"},{"label":"London","value":"Europe/London"},{"label":"Los_Angeles","value":"America/Los_Angeles"},{"label":"Luxembourg","value":"Europe/Luxembourg"},{"label":"Madrid","value":"Europe/Madrid"},{"label":"Magadan","value":"Asia/Magadan"},{"label":"Managua","value":"America/Managua"},{"label":"Mazatlan","value":"America/Mazatlan"},{"label":"Mexico_City","value":"America/Mexico_City"},{"label":"Midway","value":"Pacific/Midway"},{"label":"Mogadishu","value":"Africa/Mogadishu"},{"label":"Montevideo","value":"America/Montevideo"},{"label":"Montreal","value":"America/Montreal"},{"label":"Moscow","value":"Europe/Moscow"},{"label":"Muscat","value":"Asia/Muscat"},{"label":"Nairobi","value":"Africa/Nairobi"},{"label":"New_York","value":"America/New_York"},{"label":"Nicosia","value":"Asia/Nicosia"},{"label":"Nouakchott","value":"Africa/Nouakchott"},{"label":"Noumea","value":"Pacific/Noumea"},{"label":"Novosibirsk","value":"Asia/Novosibirsk"},{"label":"Oslo","value":"Europe/Oslo"},{"label":"Pago_Pago","value":"Pacific/Pago_Pago"},{"label":"Panama","value":"America/Panama"},{"label":"Paris","value":"Europe/Paris"},{"label":"Perth","value":"Australia/Perth"},{"label":"Phoenix","value":"America/Phoenix"},{"label":"Port_Moresby","value":"Pacific/Port_Moresby"},{"label":"Prague","value":"Europe/Prague"},{"label":"Puerto_Rico","value":"America/Puerto_Rico"},{"label":"Qatar","value":"Asia/Qatar"},{"label":"Regina","value":"America/Regina"},{"label":"Reykjavik","value":"Atlantic/Reykjavik"},{"label":"Riyadh","value":"Asia/Riyadh"},{"label":"Rome","value":"Europe/Rome"},{"label":"Saigon","value":"Asia/Saigon"},{"label":"Santiago","value":"America/Santiago"},{"label":"Sao_Paulo","value":"America/Sao_Paulo"},{"label":"Seoul","value":"Asia/Seoul"},{"label":"Shanghai","value":"Asia/Shanghai"},{"label":"Singapore","value":"Asia/Singapore"},{"label":"Sofia","value":"Europe/Sofia"},{"label":"SST","value":"SST"},{"label":"St_Johns","value":"America/St_Johns"},{"label":"Stockholm","value":"Europe/Stockholm"},{"label":"Sydney","value":"Australia/Sydney"},{"label":"Taipei","value":"Asia/Taipei"},{"label":"Tashkent","value":"Asia/Tashkent"},{"label":"Tegucigalpa","value":"America/Tegucigalpa"},{"label":"Tehran","value":"Asia/Tehran"},{"label":"Tijuana","value":"America/Tijuana"},{"label":"Tokyo","value":"Asia/Tokyo"},{"label":"Tripoli","value":"Africa/Tripoli"},{"label":"Tunis","value":"Africa/Tunis"},{"label":"UTC","value":"UTC"},{"label":"Vancouver","value":"America/Vancouver"},{"label":"Vienna","value":"Europe/Vienna"},{"label":"Vladivostok","value":"Asia/Vladivostok"},{"label":"Warsaw","value":"Europe/Warsaw"},{"label":"Winnipeg","value":"America/Winnipeg"},{"label":"Yakutsk","value":"Asia/Yakutsk"},{"label":"Yekaterinburg","value":"Asia/Yekaterinburg"},{"label":"Zurich","value":"Europe/Zurich"}],
            meetingInstanceId: null,
            submissionsInstanceId: null,
            submissions: null,
            showEditMeetingModal: false,
            editMeetingId: null,
            editMeetingInstance: null,
            editMeetingForm: {
                title: '',
                day: null,
                date: '',
                startTime: '',
                endTime: '',
                hostId: ''
            },
            meetingForm: {
                title: '',
                day: null,
                date: '',
                startTime: '',
                endTime: '',
                hostId: '',
                timezone: 'America/Vancouver'
            },
            downloadForm: {
                name: '',
                useUrl: false,
                url: '',
                file: null,
                fileName: null,
                thumbnail: null,
                thumbnailName: null
            },
            lessonForm: {
                name: '',
                title: '',
                description: ''
            },
            videoForm: {
                title: '',
                file: null,
                fileName: null
            },
            deliverableForm: {
                name: '',
                description: '',
                isFinal: false
            },
            form: {
                name: '',
                duration: null,
                introVideoFile: null,
                introVideoFileName: null
            },
            showEditCourseModal: false,
            editCourseId: null,
            editCourseForm: {
                introVideoFile: null,
                introVideoFileName: null,
                existingIntroVideoUrl: null,
                introVideoPreviewUrl: null
            },
            videoPlayerVjsOptions: {
                fluid: true,
                playbackRates: [0.7, 1.0, 1.25, 1.5, 2.0, 3.0],
                plugins: {
                    framebyframe: {
                        fps: 23.98,
                        steps: [
                            { icon: 'fa-undo', step: -10 },
                            // { icon: 'fa-step-backward', step: -1 },
                            // { icon: 'fa-step-forward', step: 1 },
                            { icon: 'fa-repeat', step: 10 },
                        ]
                    },
                },
            },
        }
    },
    components: {
        PolyVideo,
        HtmlEditor,        
        draggable
    },
    created() {

    },
    mounted() {
        this.getCourses();
    },
    methods: {
        getCourses() {
            trialCourses.getCourses().then((response) => {
                if (response.data.success) {
                    this.courses = response.data.courses;
                } else {
                    toastr.error('Error loading courses')
                }
            });
        },
        
        openCourse(course) {
            this.selectedCourse = course;
            this.selectedLesson = null;
        },
        openLesson(lesson) {
            this.selectedLesson = lesson;
            this.selectedVideo = null;
            this.lessonTab = 'information';
            this.lessonForm.name = lesson.name || '';
            this.lessonForm.title = lesson.title || '';
            this.lessonForm.description = lesson.description || '';
            this.syncDeliverableForm(lesson.deliverable);
            this.$nextTick(() => {
                if (this.$refs.lessonEditor) {
                    this.$refs.lessonEditor.setContent(lesson.description || '');
                }
                if (lesson.deliverable && this.$refs.deliverableEditor) {
                    this.$refs.deliverableEditor.setContent(lesson.deliverable.description || '');
                }
            });
        },
        openVideo(video) {
            this.selectedVideo = video;
            this.videoForm.title = video.title || '';
            this.videoForm.file = null;
            const filename = `${this.selectedVideo.url}.mp4`
            
            aws.getPresignedUrl(this, 'tto-trial-course-videos-raw', filename, 'getObject').then((response)=>{                
                this.videoUrl = response.data;
            });           
            trialCourses.getTranscript(video.id).then((response)=>{
                this.selectedVideo.transcript = response.data.transcript;
            }) 
        },
        openNotes(notes) {
            this.notesModalText = notes;
            this.showNotesModal = true;
        },
        clearFile() {
            this.videoForm.file = null;
            this.videoForm.fileName = null;
        },
        setFile(e) {
            
            if (e.target.files.length) {
                this.videoForm.file = e.target.files[0];
                this.videoForm.fileName = e.target.files[0].name;
            } else {
                this.clearFile();
            }
        },
        saveVideo() {
            const file = this.videoForm.file;
            const updateData = { title: this.videoForm.title };

            const persist = (extraData) => {
                Object.assign(updateData, extraData, { id: this.selectedVideo.id, lessonId: this.selectedLesson.id });
                trialCourses.upsertVideo(updateData).then((response) => {
                    if (response.data.success) {
                        Object.assign(this.selectedVideo, response.data.video);
                        this.clearFile();
                        if (response.data.video.url) {                            
                            aws.getPresignedUrl(this, 'tto-trial-course-videos-raw', `${response.data.video.url}.mp4`, 'getObject').then((response)=>{                
                                this.videoUrl = response.data;
                            });            
                        }
                        toastr.success('Video saved');
                    } else {
                        toastr.error('Error saving video');
                    }
                });
            };

            if (file) {
                const uuid = uuidV4();
                const filename = uuid + '.' + file.name.split('.').pop();
                const bucket = 'tto-trial-course-videos-raw';

                aws.signature(file, filename, bucket).then((signature) => {
                    aws.getPresignedUrl(this, bucket, filename, 'putObject').then((presignedUrl) => {
                        const acceleratedUrl = presignedUrl.data.replace('s3.us-west-2','s3-accelerate');
                        aws.upload(file, filename, signature, bucket, acceleratedUrl).then((response) => {
                            if (response.success === true) {
                                persist({ url: uuid });
                            } else {
                                toastr.error('Error uploading video file');
                            }
                        });
                    });
                });
            } else {
                persist({});
            }
        },
        reorderVideos() {
            this.$nextTick(()=>{
                const updates = this.selectedLesson.videos.map((video, index) => {
                    return trialCourses.upsertVideo({ id: video.id, lessonId: this.selectedLesson.id, order: index + 1 });
                });
                const map = this.selectedLesson.videos.map((v, index)=>{
                    return {
                        title: v.title,
                        order: index + 1
                    }
                })
                
                Promise.all(updates).then(() => {
                    toastr.success('Video order saved');
                }).catch(() => {
                    toastr.error('Error saving video order');
                });
            });
        },
        setAddVideoFile(e) {
            if (e.target.files.length) {
                this.addVideoForm.file = e.target.files[0];
                this.addVideoForm.fileName = e.target.files[0].name;
            } else {
                this.clearAddVideoFile();
            }
        },
        clearAddVideoFile() {
            this.addVideoForm.file = null;
            this.addVideoForm.fileName = null;
        },
        deleteVideo(video) {
            this.$swal({
                title: 'Are you sure?',
                text: 'This will permanently delete this video.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'Cancel'
            }).then(() => {
                trialCourses.deleteVideo(video.id).then((response) => {
                    if (response.data.success) {
                        const index = this.selectedLesson.videos.indexOf(video);
                        this.selectedLesson.videos.splice(index, 1);
                        toastr.success('Video deleted');
                    } else {
                        toastr.error('Error deleting video');
                    }
                });
            }).catch(() => {});
        },
        submitAddVideo() {
            const file = this.addVideoForm.file;
            const title = this.addVideoForm.title;
            const existingVideoCount = this.selectedLesson.videos ? this.selectedLesson.videos.length : 0;

            const persist = (extraData) => {
                const data = { title, lessonId: this.selectedLesson.id, existingVideoCount, ...extraData };
                trialCourses.upsertVideo(data).then((response) => {
                    if (response.data.success) {
                        if (!this.selectedLesson.videos) {
                            this.$set(this.selectedLesson, 'videos', []);
                        }
                        this.selectedLesson.videos.push(response.data.video);
                        this.showAddVideoModal = false;
                        this.addVideoForm.title = '';
                        this.clearAddVideoFile();
                        toastr.success('Video added');
                    } else {
                        toastr.error('Error adding video');
                    }
                });
            };

            if (file) {
                const uuid = uuidV4();
                const filename = uuid + '.' + file.name.split('.').pop();
                const bucket = 'tto-trial-course-videos-raw';

                aws.signature(file, filename, bucket).then((signature) => {
                    aws.getPresignedUrl(this, bucket, filename, 'putObject').then((presignedUrl) => {
                        const acceleratedUrl = presignedUrl.data.replace('s3.us-west-2','s3-accelerate');
                        aws.upload(file, filename, signature, bucket, acceleratedUrl).then((response) => {
                            if (response.success === true) {
                                persist({ url: uuid });
                            } else {
                                toastr.error('Error uploading video file');
                            }
                        });
                    });
                });
            } else {
                persist({});
            }
        },
        syncDeliverableForm(deliverable) {
            if (!deliverable) return;
            this.deliverableForm.name = deliverable.name || '';
            this.deliverableForm.description = deliverable.description || '';
            this.deliverableForm.isFinal = deliverable.isFinal || false;
            this.$nextTick(() => {
                if (this.$refs.deliverableEditor) {
                    this.$refs.deliverableEditor.setContent(deliverable.description || '');
                }
            });
        },
        saveDeliverable() {
            this.deliverableForm.description = this.$refs.deliverableEditor.getContent();
            trialCourses.upsertDeliverable({
                id: this.selectedLesson.deliverable.id,
                lessonId: this.selectedLesson.id,
                courseId: this.selectedCourse.id,
                ...this.deliverableForm
            }).then((response) => {
                if (response.data.success) {
                    Object.assign(this.selectedLesson.deliverable, this.deliverableForm);
                    this.$forceUpdate();
                    toastr.success('Deliverable saved');
                } else {
                    toastr.error('Error saving deliverable');
                }
            });
        },
        addDeliverable() {
            let name;
            if (this.selectedLesson.day === 0) {
                name = 'Pre-course Deliverable';
            } else {
                name = `Day ${this.selectedLesson.day} Deliverable`;            
            }
            trialCourses.upsertDeliverable({
                    name,
                    lessonId: this.selectedLesson.id, 
                    courseId: this.selectedCourse.id,                     
                }).then((response) => {
                if (response.data.success) {
                    toastr.success('Deliverable added');
                    console.log(response.data.deliverable)
                    this.selectedLesson.deliverable = response.data.deliverable;
                    this.syncDeliverableForm(response.data.deliverable);
                    this.lessonTab = 'deliverable';
                    this.$forceUpdate();
                } else {
                    toastr.error('Error adding deliverable');
                }
            });
        },
        deleteDeliverable() {
            this.$swal({
                title: 'Are you sure?',
                text: 'This will delete the deliverable and all associated submissions.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'Cancel'
            }).then((result) => {
                trialCourses.deleteDeliverable(this.selectedLesson.deliverable.id).then((response) => {
                    if (response.data.success) {
                        toastr.success('Deliverable deleted');
                        this.selectedLesson.deliverable = null;
                        this.syncDeliverableForm({});
                        this.lessonTab = 'information';
                        this.$forceUpdate();
                    } else {
                        toastr.error('Error deleting deliverable');
                    }
                });
            }).catch((e)=>{
                // do nothing
            });          
        },
        addMeeting(instance) {
            if (!this.staffMembers) {
                users.getAll(this, 'OnlineDirector', null).then((response)=>{
                    this.staffMembers = response.data.sort((a,b)=>{
                        return a.firstName.trim().localeCompare(b.firstName.trim()) || a.lastName.trim().localeCompare(b.lastName.trim());
                    });
                });
            }
            this.meetingInstanceId = instance.id;
            this.meetingForm.title = '';
            this.meetingForm.day = '';
            this.meetingForm.date = '';
            this.meetingForm.startTime = '';
            this.meetingForm.endTime = '';
            this.meetingForm.hostId = '';
            this.meetingForm.timezone = 'America/Vancouver';
            this.showAddMeetingModal = true;
        },
        editMeeting(meeting, instance) {
            if (!this.staffMembers) {
                users.getAll(this, 'OnlineDirector', null).then((response)=>{
                    this.staffMembers = response.data.sort((a,b)=>{
                        return a.firstName.trim().localeCompare(b.firstName.trim()) || a.lastName.trim().localeCompare(b.lastName.trim());
                    });
                });
            }
            this.editMeetingId = meeting.id;
            this.editMeetingInstance = instance;
            this.editMeetingForm.title = meeting.title || '';
            this.editMeetingForm.day = meeting.day != null ? meeting.day : null;
            this.editMeetingForm.date = meeting.date ? meeting.date.substring(0, 10) : '';
            this.editMeetingForm.startTime = new Date(meeting.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: meeting.timezone });
            this.editMeetingForm.endTime = new Date(meeting.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: meeting.timezone });
            this.editMeetingForm.hostId = meeting.hostId || '';
            this.showEditMeetingModal = true;
        },
        submitEditMeeting() {
            if (this.editMeetingForm.endTime <= this.editMeetingForm.startTime) {
                toastr.error('End time must be after start time');
                return;
            }
            let hostId = this.editMeetingForm.hostId;
            if (hostId.includes('@')) {
                const [localPart, domain] = hostId.split('@');
                const cleanLocalPart = localPart.split('+')[0];
                hostId = `${cleanLocalPart}@${domain}`;
            }
            trialCourses.updateMeeting(this.editMeetingId, {
                hostId,
                title: this.editMeetingForm.title,
                ...(this.editMeetingForm.day === 0 ? { date: this.editMeetingForm.date } : {}),
                startTime: this.editMeetingForm.startTime,
                endTime: this.editMeetingForm.endTime
            }).then((response) => {
                if (response.data.success) {
                    const meetings = this.editMeetingInstance.meetings;
                    const index = meetings.findIndex(m => m.id === this.editMeetingId);
                    if (index !== -1) {
                        this.$set(meetings, index, response.data.meeting);
                    }
                    this.showEditMeetingModal = false;
                    toastr.success('Live session updated');
                } else {
                    toastr.error('Error updating live session');
                }
            });
        },
        deleteMeeting(meeting, instance) {
            this.$swal({
                title: 'Are you sure?',
                text: 'This will delete this live session.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'Cancel'
            }).then(() => {
                trialCourses.deleteMeeting(meeting.id).then((response) => {
                    if (response.data.success) {
                        const index = instance.meetings.indexOf(meeting);
                        instance.meetings.splice(index, 1);
                        toastr.success('Live session deleted');
                    } else {
                        toastr.error('Error deleting live session');
                    }
                });
            }).catch(() => {});
        },
        formatMeetingRange(meeting) {
            const tz = meeting.timezone;
            const opts = { timeZone: tz };
            const start = new Date(meeting.startTime);
            const end = new Date(meeting.endTime);
            const sameDay = start.toLocaleDateString([], opts) === end.toLocaleDateString([], opts);
            const startStr = start.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short', timeZone: tz });
            const endStr = sameDay
                ? end.toLocaleTimeString([], { timeStyle: 'short', timeZone: tz })
                : end.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short', timeZone: tz });
            return `${startStr} – ${endStr} (${tz.split('/')[1]})`;
        },
        onMeetingDayChange() {
            const day = this.meetingForm.day;
            if (this.meetingForm.title === '') {
                this.meetingForm.title = day === 0 ? 'Pre-course live session' : `Day ${day} live session`;
            }
        },
        autoSetEndTime() {
            if (!this.meetingForm.startTime) return;
            const [h, m] = this.meetingForm.startTime.split(':').map(Number);
            const pad = n => String(n).padStart(2, '0');
            this.meetingForm.endTime = `${pad((h + 1) % 24)}:${pad(m)}`;
        },
        submitAddMeeting() {
            if (this.meetingForm.endTime <= this.meetingForm.startTime) {
                toastr.error('End time must be after start time');
                return;
            }
            let hostId = this.meetingForm.hostId
            // remove any +-address segments, like user+work@domain.com
            if (hostId.includes('@')) {
                const [localPart, domain] = hostId.split('@');
                const cleanLocalPart = localPart.split('+')[0];
                hostId = `${cleanLocalPart}@${domain}`;
            }

            trialCourses.addMeeting({
                hostId,
                title: this.meetingForm.title,
                courseId: this.selectedCourse.id,
                instanceId: this.meetingInstanceId,
                day: this.meetingForm.day,
                ...(this.meetingForm.day === 0 ? { date: this.meetingForm.date } : {}),
                startTime: this.meetingForm.startTime,
                endTime: this.meetingForm.endTime,
                timezone: this.meetingForm.timezone
            }).then((response) => {
                if (response.data.success) {
                    const instance = this.selectedCourse.instances.find(i => i.id === this.meetingInstanceId);
                    if (instance) {
                        if (!instance.meetings) this.$set(instance, 'meetings', []);
                        instance.meetings.push(response.data.meeting);
                    }
                    this.showAddMeetingModal = false;
                    toastr.success('Live session added');
                } else {
                    toastr.error('Error adding live session');
                }
            });
        },
        scheduleCourse() {
            this.scheduleCourseId = this.selectedCourse.id;
            this.scheduleForm.startDate = '';
            this.scheduleForm.slots = null;
            this.showScheduleModal = true;
        },
        submitSchedule() {
            trialCourses.createInstance({
                courseId: this.scheduleCourseId,
                startDate: this.scheduleForm.startDate,
                slots: this.scheduleForm.slots,
                startTime: this.scheduleForm.startTime,
                timezone: this.scheduleForm.timezone
            }).then((response) => {
                if (response.data.success) {
                    if (!this.selectedCourse.instances) {
                        this.$set(this.selectedCourse, 'instances', []);
                    }
                    this.selectedCourse.instances.push(response.data.instance);
                    this.showScheduleModal = false;
                    toastr.success('Course instance scheduled');
                } else {
                    toastr.error('Error scheduling course instance');
                }
            });
        },
        editInstance(instance) {
            this.editInstanceId = instance.id;
            this.editInstanceForm.startDate = instance.startDate.slice(0, 10);
            this.editInstanceForm.slots = instance.slots;
            this.showEditInstanceModal = true;
        },
        submitEditInstance() {
            trialCourses.updateInstance(this.editInstanceId, {
                startDate: this.editInstanceForm.startDate,
                slots: this.editInstanceForm.slots
            }).then((response) => {
                if (response.data.success) {
                    const instance = this.selectedCourse.instances.find(i => i.id === this.editInstanceId);
                    if (instance) {
                        Object.assign(instance, response.data.instance);
                    }
                    this.showEditInstanceModal = false;
                    toastr.success('Instance updated');
                } else {
                    toastr.error('Error updating instance');
                }
            });
        },
        deleteInstance(instance) {
            if (instance.slotsFilled > 0) {
                toastr.error('Cannot delete an instance with enrolled students');
                return;
            }
            this.$swal({
                title: 'Are you sure?',
                text: 'This will delete this course instance.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'Cancel'
            }).then(() => {
                trialCourses.deleteInstance(instance.id).then((response) => {
                    if (response.data.success) {
                        const index = this.selectedCourse.instances.indexOf(instance);
                        this.selectedCourse.instances.splice(index, 1);
                        toastr.success('Instance deleted');
                    } else {
                        toastr.error('Error deleting instance');
                    }
                });
            }).catch(() => {});
        },
        openSubmissions(instance) {
            if (this.submissionsInstanceId === instance.id) {
                this.submissionsInstanceId = null;
                this.submissions = null;
                return;
            }
            this.submissionsInstanceId = instance.id;
            this.submissions = null;
            trialCourses.getSubmissions(instance.id).then((response) => {
                if (response.data.success) {
                    this.submissions = response.data.submissions;
                } else {
                    toastr.error('Error loading submissions');
                }
            });
        },
        addDownload() {
            this.downloadForm.name = '';
            this.downloadForm.useUrl = false;
            this.downloadForm.url = '';
            this.downloadForm.file = null;
            this.downloadForm.fileName = null;
            this.downloadForm.thumbnail = null;
            this.downloadForm.thumbnailName = null;
            this.showAddDownloadModal = true;
        },
        setDownloadFile(e) {
            if (e.target.files.length) {
                this.downloadForm.file = e.target.files[0];
                this.downloadForm.fileName = e.target.files[0].name;
            } else {
                this.clearDownloadFile();
            }
        },
        clearDownloadFile() {
            this.downloadForm.file = null;
            this.downloadForm.fileName = null;
        },
        setThumbnailFile(e) {
            if (e.target.files.length) {
                this.downloadForm.thumbnail = e.target.files[0];
                this.downloadForm.thumbnailName = e.target.files[0].name;
            } else {
                this.clearThumbnailFile();
            }
        },
        clearThumbnailFile() {
            this.downloadForm.thumbnail = null;
            this.downloadForm.thumbnailName = null;
        },
        uploadThumbnailIfNeeded() {
            const thumbnail = this.downloadForm.thumbnail;
            if (!thumbnail) return Promise.resolve(null);
            const bucket = 'tto-trial-course-downloads';
            const filename = 'thumb-' + uuidV4() + '.' + thumbnail.name.split('.').pop();
            return aws.signature(thumbnail, filename, bucket).then((signature) => {
                return aws.getPresignedUrl(this, bucket, filename, 'putObject').then((presignedUrl) => {
                    const acceleratedUrl = presignedUrl.data.replace('s3.us-west-2', 's3-accelerate');
                    return aws.upload(thumbnail, filename, signature, bucket, acceleratedUrl).then((response) => {
                        if (response.success === true) return filename;
                        throw new Error('Thumbnail upload failed');
                    });
                });
            });
        },
        downloadFile(key) {
            aws.getPresignedUrl(this, 'tto-trial-course-downloads', key, 'getObject').then((presignedUrl) => {
                window.open(presignedUrl.data, '_blank');
            });
        },
        downloadSubmission(submission) {
            const key = submission.url.split('/').pop();
            aws.getPresignedUrl(this, 'tto-trial-course-submissions', key, 'getObject').then((response) => {
                window.open(response.data, '_blank');
            });
        },
        async downloadAllSubmissions(group) {
            const bucket = 'tto-trial-course-submissions';
            const zipFilename = `${group.name}.zip`;

            let aborted = false;
            let writer = null;

            try {
                const urlEntries = await Promise.all(
                    group.items.map(submission => {
                        const key = submission.url.split('/').pop();
                        return aws.getPresignedUrl(this, bucket, key, 'getObject').then(r => ({ key, url: r.data }));
                    })
                );

                const fileStream = streamSaver.createWriteStream(zipFilename);
                writer = fileStream.getWriter();

                const zip = new fflate.Zip((err, chunk, final) => {
                    if (aborted) return;
                    if (err) { writer.abort(); throw err; }
                    writer.write(chunk).catch(() => { aborted = true; });
                    if (final) { writer.close().catch(() => {}); }
                });

                for (const { key, url } of urlEntries) {
                    if (aborted) break;

                    const filename = key.split('/').pop();
                    const response = await fetch(url);
                    const contentLength = parseInt(response.headers.get('content-length') || '0', 10);

                    const ext = filename.split('.').pop().toLowerCase();
                    const alreadyCompressed = ['zip', 'gz', 'jpg', 'jpeg', 'png', 'gif', 'mp4', 'webm', 'mp3', 'pdf'].includes(ext);

                    const file = alreadyCompressed
                        ? new fflate.ZipPassThrough(filename)
                        : new fflate.ZipDeflate(filename, { level: 6 });

                    if (contentLength > 0xffffffff) file.zip64 = true;
                    zip.add(file);

                    const reader = response.body.getReader();
                    try {
                        while (true) {
                            if (aborted) { reader.cancel(); break; }
                            const { done, value } = await reader.read();
                            if (done) { file.push(new Uint8Array(0), true); break; }
                            file.push(value, false);
                        }
                    } catch (readError) {
                        if (!aborted) throw readError;
                    }
                }

                if (!aborted) {
                    zip.end();
                    toastr.success('Download complete!');
                }
            } catch (error) {
                if (!aborted) {
                    console.error('Error creating zip:', error);
                    toastr.error('Error creating zip file');
                }
            } finally {
                if (aborted && writer) {
                    try { writer.abort(); } catch (e) {}
                }
            }
        },
        downloadSubmissionsSheet(group) {
            const rows = [['Name', 'Date/Time Submitted', 'Filename']];
            for (const s of group.items) {
                const name = `${s.user.firstName} ${s.user.lastName}`;
                const date = new Date(s.createdAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
                const filename = s.url.split('/').pop();
                rows.push([name, date, filename]);
            }
            const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${group.name}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        },
        submitAddDownload() {
            if (this.downloadForm.useUrl) {
                this.uploadThumbnailIfNeeded().then((thumbnailKey) => {
                    const payload = {
                        lessonId: this.selectedLesson.id,
                        name: this.downloadForm.name,
                        url: this.downloadForm.url
                    };
                    if (thumbnailKey) payload.thumbnail = thumbnailKey;
                    trialCourses.addDownload(payload).then((response) => {
                        if (response.data.success) {
                            this.showAddDownloadModal = false;
                            this.selectedLesson.downloads.push(response.data.download);
                            toastr.success('Download added');
                        } else {
                            toastr.error('Error adding download');
                        }
                    });
                }).catch(() => toastr.error('Error uploading thumbnail'));
                return;
            }

            const file = this.downloadForm.file;
            if (!file) {
                toastr.error('Please select a file');
                return;
            }
            const bucket = 'tto-trial-course-downloads';
            const filename = uuidV4() + '.' + file.name.split('.').pop();

            aws.signature(file, filename, bucket).then((signature) => {
                aws.getPresignedUrl(this, bucket, filename, 'putObject').then((presignedUrl) => {
                    const acceleratedUrl = presignedUrl.data.replace('s3.us-west-2','s3-accelerate');
                    console.log(presignedUrl.data)
                    aws.upload(file, filename, signature, bucket, acceleratedUrl).then((response) => {
                        if (response.success === true) {
                            this.uploadThumbnailIfNeeded().then((thumbnailKey) => {
                                const payload = {
                                    lessonId: this.selectedLesson.id,
                                    name: this.downloadForm.name,
                                    url: filename
                                };
                                if (thumbnailKey) payload.thumbnail = thumbnailKey;
                                trialCourses.addDownload(payload).then((response) => {
                                    if (response.data.success) {
                                        this.showAddDownloadModal = false;
                                        this.clearDownloadFile();
                                        this.selectedLesson.downloads.push(response.data.download);
                                        toastr.success('Download added');
                                    } else {
                                        toastr.error('Error adding download');
                                    }
                                });
                            }).catch(() => toastr.error('Error uploading thumbnail'));
                        } else {
                            toastr.error('Error uploading file');
                        }
                    });
                });
            });
        },
        saveDownload(download) {
            trialCourses.updateDownload(download.id, { name: download.name }).then((response) => {
                if (response.data.success) {
                    toastr.success('Download saved');
                } else {
                    toastr.error('Error saving download');
                }
            });
        },
        setDownloadThumbnail(download, event) {
            const file = event.target.files[0];
            if (!file) return;
            const bucket = 'tto-trial-course-downloads';
            const filename = 'thumb-' + uuidV4() + '.' + file.name.split('.').pop();
            aws.signature(file, filename, bucket).then((signature) => {
                aws.getPresignedUrl(this, bucket, filename, 'putObject').then((presignedUrl) => {
                    const acceleratedUrl = presignedUrl.data.replace('s3.us-west-2', 's3-accelerate');
                    aws.upload(file, filename, signature, bucket, acceleratedUrl).then((response) => {
                        if (response.success === true) {
                            trialCourses.updateDownload(download.id, { thumbnail: filename }).then((response) => {
                                if (response.data.success) {
                                    this.$set(download, 'thumbnail', filename);
                                    toastr.success('Thumbnail saved');
                                } else {
                                    toastr.error('Error saving thumbnail');
                                }
                            });
                        } else {
                            toastr.error('Error uploading thumbnail');
                        }
                    });
                });
            });
        },
        deleteDownload(download) {
            this.$swal({
                title: 'Are you sure?',
                text: 'This will delete the download permanently.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'Cancel'
            }).then((result) => {

                trialCourses.deleteDownload(download.id).then((response) => {
                    if (response.data.success) {
                        const index = this.selectedLesson.downloads.indexOf(download);
                        this.selectedLesson.downloads.splice(index, 1);
                        toastr.success('Download deleted');
                    } else {
                        toastr.error('Error deleting download');
                    }
                });
            }).catch((e)=>{
                // do nothing
            })
        },
        openAddNotificationModal() {
            this.notificationForm.header = '';
            this.notificationForm.color = null;
            this.showAddNotificationModal = true;
            this.$nextTick(() => {
                if (this.$refs.notificationEditor) {
                    this.$refs.notificationEditor.setContent('');
                }
            });
        },
        submitAddNotification() {
            const text = this.$refs.notificationEditor ? this.$refs.notificationEditor.getContent() : '';
            trialCourses.addNotification({
                courseId: this.selectedCourse.id,
                lessonId: this.selectedLesson.id,
                header: this.notificationForm.header,
                text,
                color: this.notificationForm.color
            }).then((response) => {
                if (response.data.success) {
                    if (!this.selectedLesson.notifications) {
                        this.$set(this.selectedLesson, 'notifications', []);
                    }
                    this.selectedLesson.notifications.push(response.data.notification);
                    this.showAddNotificationModal = false;
                    toastr.success('Notification added');
                } else {
                    toastr.error('Error adding notification');
                }
            });
        },
        openEditNotificationModal(notification) {
            this.editNotificationId = notification.id;
            this.editNotificationRef = notification;
            this.editNotificationForm.header = notification.header || '';
            this.editNotificationForm.color = notification.color || null;
            this.showEditNotificationModal = true;
            this.$nextTick(() => {
                if (this.$refs.editNotificationEditor) {
                    this.$refs.editNotificationEditor.setContent(notification.text || '');
                }
            });
        },
        submitEditNotification() {
            const text = this.$refs.editNotificationEditor ? this.$refs.editNotificationEditor.getContent() : '';
            trialCourses.updateNotification({
                header: this.editNotificationForm.header,
                text,
                color: this.editNotificationForm.color
            }, this.editNotificationId).then((response) => {
                if (response.data.success) {
                    Object.assign(this.editNotificationRef, { header: this.editNotificationForm.header, text, color: this.editNotificationForm.color });
                    this.showEditNotificationModal = false;
                    toastr.success('Notification updated');
                } else {
                    toastr.error('Error updating notification');
                }
            });
        },
        deleteNotification(notification) {
            this.$swal({
                title: 'Are you sure?',
                text: 'This will delete this notification.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'Cancel'
            }).then(() => {
                trialCourses.deleteNotification(notification.id).then((response) => {
                    if (response.data.success) {
                        const index = this.selectedLesson.notifications.indexOf(notification);
                        this.selectedLesson.notifications.splice(index, 1);
                        toastr.success('Notification deleted');
                    } else {
                        toastr.error('Error deleting notification');
                    }
                });
            }).catch(() => {});
        },
        saveLesson() {
            this.lessonForm.description = this.$refs.lessonEditor.getContent();
            trialCourses.updateLesson(this.selectedLesson.id, this.lessonForm).then((response) => {
                if (response.data.success) {
                    toastr.success('Lesson saved');
                    Object.assign(this.selectedLesson, this.lessonForm);
                } else {
                    toastr.error('Error saving lesson');
                }
            });
        },
        setCreateCourseIntroVideo(e) {
            if (e.target.files.length) {
                this.form.introVideoFile = e.target.files[0];
                this.form.introVideoFileName = e.target.files[0].name;
            } else {
                this.clearCreateCourseIntroVideo();
            }
        },
        clearCreateCourseIntroVideo() {
            this.form.introVideoFile = null;
            this.form.introVideoFileName = null;
        },
        createCourse() {
            const description = this.$refs.createCourseEditor ? this.$refs.createCourseEditor.getContent() : '';
            const file = this.form.introVideoFile;

            const persist = (extraData) => {
                trialCourses.createCourse({
                    name: this.form.name,
                    duration: this.form.duration,
                    description,
                    ...extraData
                }).then(() => {
                    this.form.name = '';
                    this.form.duration = null;
                    this.clearCreateCourseIntroVideo();
                    if (this.$refs.createCourseEditor) this.$refs.createCourseEditor.setContent('');
                    this.showCreateModal = false;
                    this.getCourses();
                });
            };

            if (file) {
                const uuid = uuidV4();
                const filename = uuid + '.' + file.name.split('.').pop();
                const bucket = 'tto-trial-course-videos-raw';

                aws.signature(file, filename, bucket).then((signature) => {
                    aws.getPresignedUrl(this, bucket, filename, 'putObject').then((presignedUrl) => {
                        const acceleratedUrl = presignedUrl.data.replace('s3.us-west-2','s3-accelerate');
                        aws.upload(file, filename, signature, bucket, acceleratedUrl).then((response) => {
                            if (response.success === true) {
                                persist({ introVideoUrl: uuid });
                            } else {
                                toastr.error('Error uploading intro video');
                            }
                        });
                    });
                });
            } else {
                persist({});
            }
        },
        editCourse(course) {
            this.editCourseId = course.id;
            this.editCourseForm.introVideoFile = null;
            this.editCourseForm.introVideoFileName = null;
            const introVideo = course.introVideos && course.introVideos[0];
            this.editCourseForm.existingIntroVideoUrl = introVideo ? introVideo.url : null;
            this.editCourseForm.introVideoPreviewUrl = null;
            this.showEditCourseModal = true;

            this.$nextTick(() => {
                if (this.$refs.editCourseEditor) {
                    this.$refs.editCourseEditor.setContent(course.description || '');
                }
            });

            if (introVideo) {
                const filename = `${introVideo.url}.mp4`;
                aws.getPresignedUrl(this, 'tto-trial-course-videos-raw', filename, 'getObject').then((response) => {
                    this.editCourseForm.introVideoPreviewUrl = response.data;
                });
            }
        },
        setEditCourseIntroVideo(e) {
            if (e.target.files.length) {
                this.editCourseForm.introVideoFile = e.target.files[0];
                this.editCourseForm.introVideoFileName = e.target.files[0].name;
            } else {
                this.clearEditCourseIntroVideo();
            }
        },
        clearEditCourseIntroVideo() {
            this.editCourseForm.introVideoFile = null;
            this.editCourseForm.introVideoFileName = null;
        },
        submitEditCourse() {
            const description = this.$refs.editCourseEditor ? this.$refs.editCourseEditor.getContent() : '';
            const file = this.editCourseForm.introVideoFile;

            const persist = (extraData) => {
                trialCourses.updateCourse(this.editCourseId, {
                    description,
                    ...extraData
                }).then((response) => {
                    if (response.data.success) {
                        const course = this.courses.find(c => c.id === this.editCourseId);
                        if (course) {
                            course.description = description;
                            if (extraData.introVideoUrl) course.introVideoUrl = extraData.introVideoUrl;
                        }
                        this.showEditCourseModal = false;
                        toastr.success('Course updated');
                    } else {
                        toastr.error('Error updating course');
                    }
                });
            };

            if (file) {
                const uuid = uuidV4();
                const filename = uuid + '.' + file.name.split('.').pop();
                const bucket = 'tto-trial-course-videos-raw';

                aws.signature(file, filename, bucket).then((signature) => {
                    aws.getPresignedUrl(this, bucket, filename, 'putObject').then((presignedUrl) => {
                        const acceleratedUrl = presignedUrl.data.replace('s3.us-west-2','s3-accelerate');
                        aws.upload(file, filename, signature, bucket, acceleratedUrl).then((response) => {
                            if (response.success === true) {
                                persist({ introVideoUrl: uuid });
                            } else {
                                toastr.error('Error uploading intro video');
                            }
                        });
                    });
                });
            } else {
                persist({});
            }
        }
    },
    filters: {
        formatDateTime(value) {
            if (!value) return '';
            return new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
        },
        formatDate(value) {
            if (!value) return '';
            return new Date(value).toLocaleDateString([], { dateStyle: 'medium' });
        },
        formatInTimezone(value, timezone) {
            if (!value) return '';
            return new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short', timeZone: timezone });
        }
    },
    computed: {
        sortedLessons() {
            if (!this.selectedCourse || !this.selectedCourse.lessons) return [];
            return [...this.selectedCourse.lessons].sort((a, b) => a.day - b.day);
        },
        groupedSubmissions() {
            if (!this.submissions) return [];
            const groups = {};
            for (const s of this.submissions) {
                const name = s.deliverable ? s.deliverable.name : 'Unknown';
                if (!groups[name]) groups[name] = [];
                groups[name].push(s);
            }
            return Object.entries(groups).map(([name, items]) => ({
                name,
                items: items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            }));
        },
        colors() {
            return colors;
        }
    }
}
</script>

<style lang="scss" scoped>
    .panel-body {
        padding: 2rem!important;
    }
    input[type="time"]::-webkit-calendar-picker-indicator,
    input[type="datetime-local"]::-webkit-calendar-picker-indicator, 
    input[type="date"]::-webkit-calendar-picker-indicator {
      filter: invert(1);
      cursor: pointer;
    }
    .overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 999;
    }
    .modal-box {
        background: #222;
        padding: 2rem;
        border-radius: 4px;
        min-width: 400px;
        max-height: 90vh;
        overflow-y: auto;

    }
    .modal-box-lg {
      width:960px;

    }
    .lesson-list {
        list-style: none;
        padding: 0;
        margin-top: 1rem;
        li {
            display: flex;
            align-items: center;
            padding: 0.5rem 0;
            border-bottom: 1px solid #333;
        }
        .lesson-day {
            font-weight: bold;
            margin-right: 1rem;
            min-width: 50px;
        }
        .lesson-name {
            flex: 1;
        }
    }
    .header {
        display:flex;
        align-items:center;
        justify-content: space-between;
    }
    .well {
        position:relative;
        .btn.delete {
            position:absolute;
            top:1rem;
            right:1rem;
        }
    }
    .course-list {
        list-style: none;
        padding: 0;
        margin: 1rem 0 2rem 0;
        li {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.5rem 0;
            border-bottom: 1px solid #333;
        }
        .buttons {
            display:flex;
            align-items: center;
            gap: 1rem;
        }
    }    
    .video-upload {
        background:#151515;
        padding:3rem;
        border: 1px dashed #444;
        display:flex;
        align-item:center;
        justify-content:center;
        max-width:480px;
        margin:0 auto;
        margin-top:2rem;
        cursor:pointer;
        position: relative;
        input {            
            position:absolute;
            top:0;
            left:0;
            width:100%;
            height:100%;
            opacity:0;
        }
    }
    .filename {
        display:inline-flex;
        align-items:center;
        gap:.5rem;
        background:#111;
        border-radius:20px;
        margin-top:1rem;
        border:1px solid #444;
        padding:.5rem 2rem;
        i {
            cursor:pointer;
        }
    }
    .meeting-details {
        margin-top: 0.75rem;
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
        .label-text {
            font-weight: bold;
            margin-right: 0.5rem;
        }
    }
    .download-list {
        list-style: none;
        padding: 0;
        li {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.5rem 0;
            border-bottom: 1px solid #333;
            .form-control {
                flex: 1;
            }
        }
        .download-actions {
            display: flex;            
            gap: 0.5rem;
            flex-shrink: 0;
            label {
                top:0;
            }
        }
    }
    
    .lesson-actions {
        margin-top: 1rem;
        display: flex;
        gap: 0.5rem;
    }
    .lesson-tabs {
        list-style: none;
        padding: 0;
        display: flex;
        gap: 0;
        margin: 1.5rem 0 2rem 0;
        border-bottom: 2px solid #444;
        gap: 1rem;
        li {
            padding: 0.6rem 1.25rem;
            cursor: pointer;
            color: #999;
            border: 1px solid #444;
            border-radius: 4px 4px 0 0;
            border-bottom: 2px solid #444;
            margin-bottom: -2px;
            transition: color 0.15s, border-color 0.15s;

            &:hover {
                color: #ccc;
            }
            &.active {
                color: #fff;
                border-bottom-color: #33ccff;
            }
        }
    }
   
    .instances {
        margin-top: 2rem;
    }
    .instance {
        margin-top: 1rem;
    }
    .instance-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
    }
    .instance-details {
        display: flex;
        gap: 1.5rem;
    }
    .instance-actions {
        display: flex;
        gap: 0.5rem;
        flex-shrink: 0;
    }
    .meeting-list {
        list-style: none;
        padding: 0;
        margin-top: 0.75rem;
        li {
            padding: 0.4rem 0;
            border-top: 1px solid #333;
        }
    }
    .meeting-info {
        display: flex;
        gap: 1.5rem;
        align-items: center;
    }
    .meeting-title {
        font-weight: 600;
    }
    .meeting-time {
        color: #999;
    }
    .meeting-list li {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
    .meeting-actions {
        display: flex;
        gap: 0.5rem;
        flex-shrink: 0;
    }
    .modal-actions {
        margin-top: 1rem;
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
    }
    
    ul.videos {
        margin-bottom:1rem;
        li {
            border-bottom: 1px solid #333;
            padding:1rem 0;
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap: 0.75rem;
        }
        .video-title {
            display: flex;
            align-items:center;
            gap: 1rem;
        }
        .drag-handle {
            cursor: grab;
            color: #666;
            font-size:2rem;
            &:active { cursor: grabbing; }
        }
    }
    .btn-sm {
        padding: 6px 9px!important;
        font-size: 13px!important;
        line-height: 1.5!important;
    }
    .video-header {
        display:flex;
        align-items: center;
        justify-content:space-between;
    }
    i.toggle-transcript {
        font-size:18px;
        cursor: pointer;
    }
    .transcript {
        max-height:400px;
        overflow-y: auto;
        border: 1px solid #bbb;
        border-radius:4px;
        margin-bottom:1rem;
        padding:1rem;
    }
    .video-wrapper {

         .video {
            max-width:480px;
            margin:0 auto;
         }

    }
    .bold {
        font-weight:700!important;
    }
    .color-picker {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
        margin-top: 0.5rem;
        max-height: 220px;
        overflow-y: auto;
        padding: 0.5rem;
        background: #1a1a1a;
        border-radius: 4px;
        border: 1px solid #444;
        .color-swatch {
            width: 26px;
            height: 26px;
            border-radius: 4px;
            cursor: pointer;
            border: 2px solid transparent;
            flex-shrink: 0;
            &.selected {
                border-color: #fff;
                box-shadow: 0 0 0 2px #33ccff;
            }
            &.no-color {
                background: #333;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 10px;
                color: #aaa;
                width: auto;
                padding: 0 8px;
                white-space: nowrap;
            }
        }
    }
    .submissions {
        margin-top: 1rem;
        padding-top: 0.75rem;
        border-top: 1px solid #444;
    }
    .submission-group {
        margin-bottom: 1rem;
    }
    .submission-group-header {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin-bottom: 0.4rem;
    }
    .submission-group-name {
        font-weight: 700;
        color: #ccc;
        margin: 0;
        font-size: 0.9rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }
    .submission-list {
        list-style: none;
        padding: 0;
        margin: 0;
        li {
            display: flex;
            align-items: center;
            justify-content:space-between;
            gap: 1.5rem;
            padding: 0.4rem 0;
            border-bottom: 1px solid #2a2a2a;
            .icons {
                display:flex;
                align-items:center;
                gap: 1rem;
            }
        }
        .submission-notes {
            flex: 1;
            color: #bbb;
        }
        .meta {
            display:flex;
            align-items:center;
            gap: 1rem;
        }
        .submission-info {
            min-width: 100px;
        }
    }
    .notification-list {
        list-style: none;
        padding: 0;
        margin-top: 1rem;
        li {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.5rem 0;
            border-bottom: 1px solid #333;
        }
        .notification-color-swatch {
            width: 16px;
            height: 16px;
            border-radius: 3px;
            flex-shrink: 0;
        }
        .notification-header {
            flex: 1;
            font-weight: 600;
        }
        .notification-actions {
            display: flex;
            gap: 0.5rem;
            flex-shrink: 0;
        }
    }
</style>


// WEBPACK FOOTER //
// TrialCourses.vue?20776690